/**
 * FcmReactiveBind - Mixin reativo para Firestore com controles avançados
 *
 * Abordagem simples e desacoplada:
 * - Recebe uma CollectionReference ou DocumentReference pronta
 * - Observa mudanças na referência (não em profundidade, apenas a ref em si)
 * - Hooks com oldDoc/newDoc para contadores incrementais
 * - Agregação automática de propriedades com profundidade
 * - Suporte a espera de Promise antes de iniciar listener
 * - Zero acoplamento com lógica de queries
 *
 * @example
 * ```javascript
 * export default {
 *   data: () => ({
 *     filters: { status: 'ALL', companyId: null },
 *     operations: [],        // Declarar no data() para evitar warnings
 *     operationsAggregate: {}, // Declarar agregados também
 *     bankAccountsReady: null  // Promise para sincronização
 *   }),
 *
 *   async mounted() {
 *     // Carregar dados necessários antes do binding
 *     this.bankAccountsReady = this.loadBankAccounts();
 *     await this.bankAccountsReady;
 *   },
 *
 *   computed: {
 *     operationsRef() {
 *       let ref = firestore.collection('operations');
 *       ref = ref.where('companyId', '==', this.currentCompanyId);
 *       if (this.filters.status !== 'ALL') {
 *         ref = ref.where('status', '==', this.filters.status);
 *       }
 *       return ref.orderBy('createdAt', 'desc').limit(100);
 *     }
 *   },
 *
 *   fcmBindings() {
 *     return {
 *       operations: {
 *         ref: () => this.operationsRef,
 *         wait: this.bankAccountsReady, // Espera Promise resolver
 *         aggregate: ['amount', 'stats.total', 'stats.reconciled'],
 *         transform: (doc) => ({
 *           ...doc,
 *           bankAccount: this.bankAccounts.find(ba => ba.id === doc.bankAccountId)
 *         }),
 *         onAdd: (newDoc, oldDoc) => {
 *           this.totalCount++;
 *         },
 *         onModify: (newDoc, oldDoc) => {
 *           console.log('Changed:', oldDoc, '->', newDoc);
 *         },
 *         onRemove: (newDoc, oldDoc) => {
 *           this.totalCount--;
 *         }
 *       }
 *     };
 *   }
 * }
 * ```
 */

import {getObjectValueByPath} from '@common/ObjectUtils';

/**
 * Cria o plugin FcmReactiveBind
 */
export const fcmReactiveBindPlugin = {
  install(Vue) {
    Vue.mixin({
      beforeCreate() {
        this._fcmBindings = {};
        this._fcmUnsubscribes = {};
        this._fcmWatchers = {};
      },

      created() {
        const bindings = typeof this.$options.fcmBindings === 'function'
          ? this.$options.fcmBindings.call(this)
          : this.$options.fcmBindings;

        if (bindings) {
          Object.keys(bindings).forEach(key => {
            this.$fcmBind(key, bindings[key]);
          });
        }
      },

      beforeDestroy() {
        // Cleanup all bindings
        Object.keys(this._fcmUnsubscribes).forEach(key => {
          this.$fcmUnbind(key);
        });
      },

      methods: {
        /**
         * Bind a Firestore query to a component data property
         * @param {string} key - The data property name
         * @param {Object} options - Binding options
         */
        async $fcmBind(key, options) {
          // Unbind if already bound
          const isRebinding = !!this._fcmUnsubscribes[key];
          if (isRebinding) {
            this.$fcmUnbind(key);
          }

          // Parse options
          const config = this._fcmParseBindingOptions(key, options);

          // Initialize reactive data if not already declared in data()
          // Se já existe no data(), não precisamos fazer nada
          if (this[key] === undefined) {
            Vue.set(this, key, config.isCollection ? [] : null);
          } else if (isRebinding) {
            // Se está fazendo rebind, limpar o array/objeto existente
            if (config.isCollection !== false) {
              this[key].splice(0, this[key].length); // Limpa array mantendo reatividade
            } else {
              Vue.set(this, key, null);
            }
          }

          // Initialize aggregate object if needed
          if (config.aggregate && config.isCollection !== false) {
            this._fcmInitializeAggregate(key, config);
          }

          // Create watcher for reactive ref
          this._fcmSetupRefWatcher(key, config);

          // Start listening
          return this._fcmStartListener(key, config);
        },

        /**
         * Unbind a Firestore query
         * @param {string} key - The data property name
         */
        $fcmUnbind(key) {
          // Unsubscribe from Firestore
          if (this._fcmUnsubscribes[key]) {
            this._fcmUnsubscribes[key]();
            delete this._fcmUnsubscribes[key];
          }

          // Remove watcher
          if (this._fcmWatchers[key]) {
            this._fcmWatchers[key]();
            delete this._fcmWatchers[key];
          }

          // Clear binding config
          delete this._fcmBindings[key];
        },

        /**
         * Force rebind (useful when ref changes)
         * @param {string} key - The data property name
         */
        async $fcmRebind(key) {
          const config = this._fcmBindings[key];
          if (config) {
            return this.$fcmBind(key, config.originalOptions);
          }
        },

        /**
         * Parse binding options
         * @private
         */
        _fcmParseBindingOptions(key, options) {
          const config = {
            key,
            originalOptions: options,
            isCollection: true,
            onAdd: null,
            onModify: null,
            onRemove: null,
            onError: null,
            transform: null,
            wait: false,
            aggregate: null,
            aggregateKey: null
          };

          // Collection/Document reference function
          if (typeof options.ref === 'function') {
            config.getRef = options.ref;
          } else if (typeof options === 'function') {
            config.getRef = options;
          } else {
            throw new Error(`Invalid binding for "${key}". Must provide ref function.`);
          }

          // Detect if it's a collection or document
          // Will be determined when getting the first ref
          config.isCollection = null;

          // Hooks
          if (options.onAdd) config.onAdd = options.onAdd;
          if (options.onModify) config.onModify = options.onModify;
          if (options.onRemove) config.onRemove = options.onRemove;
          if (options.onError) config.onError = options.onError;

          // Transform function
          if (options.transform) config.transform = options.transform;

          // Wait for Promise or first data before resolving
          // Can be:
          // - true: wait for first snapshot (existing behavior)
          // - Promise: wait for promise to resolve before starting listener
          // - function returning Promise: call function and wait for promise
          if (options.wait !== undefined) config.wait = options.wait;

          // Aggregation
          if (options.aggregate) {
            config.aggregate = Array.isArray(options.aggregate) ? options.aggregate : [options.aggregate];
            config.aggregateKey = options.aggregateKey || `${key}Aggregate`;
          }

          return config;
        },

        /**
         * Setup watcher for reactive ref
         * @private
         */
        _fcmSetupRefWatcher(key, config) {
          // Watch the ref function (shallow, not deep)
          const unwatch = this.$watch(
            config.getRef,
            (newRef, oldRef) => {
              // Only rebind if ref actually changed
              if (newRef !== oldRef) {
                this.$fcmRebind(key);
              }
            }
          );

          this._fcmWatchers[key] = unwatch;
        },

        /**
         * Start Firestore listener
         * @private
         */
        async _fcmStartListener(key, config) {
          console.log('config.wait', config.wait);
          // Handle wait option - can be Promise, function returning Promise, or boolean
          if (config.wait && typeof config.wait !== 'boolean') {
            try {
              const waitPromise = typeof config.wait === 'function'
                ? config.wait.call(this)
                : config.wait;
              await waitPromise;
            } catch (error) {
              console.error(`Error waiting before starting listener for "${key}":`, error);
              throw error;
            }
          }

          return new Promise((resolve, reject) => {
            try {
              // Get reference
              const ref = config.getRef.call(this);

              if (!ref) {
                console.warn(`fcmBind: ref for "${key}" is null/undefined`);
                resolve();
                return;
              }

              // Detect if it's a collection or document
              if (config.isCollection === null) {
                config.isCollection = ref.type === 'collection' || ref.type === 'query';
              }

              // Store config
              this._fcmBindings[key] = config;

              // Start listening
              let isFirstSnapshot = true;
              this._fcmUnsubscribes[key] = ref.onSnapshot(
                (snapshot) => {
                  if (config.isCollection) {
                    this._fcmHandleCollectionSnapshot(key, snapshot, config);
                  } else {
                    this._fcmHandleDocSnapshot(key, snapshot, config);
                  }

                  // Resolve on first snapshot if wait is true (boolean)
                  if (isFirstSnapshot) {
                    isFirstSnapshot = false;
                    if (config.wait === true) {
                      resolve();
                    }
                  }
                },
                (error) => {
                  console.error(`Error in fcmBind for "${key}":`, error);
                  if (config.onError) {
                    config.onError.call(this, error);
                  }
                  reject(error);
                }
              );

              // Resolve immediately if not waiting for first snapshot
              if (config.wait !== true) {
                resolve();
              }
            } catch (error) {
              console.error(`Error setting up fcmBind for "${key}":`, error);
              reject(error);
            }
          });
        },

        /**
         * Initialize aggregate object
         * @private
         */
        _fcmInitializeAggregate(key, config) {
          if (!config.aggregate) {
            return;
          }

          const aggregateObj = {};
          config.aggregate.forEach(prop => {
            aggregateObj[prop] = 0;
          });

          // Se já existe no data(), apenas atualiza os valores
          if (this[config.aggregateKey]) {
            Object.keys(aggregateObj).forEach(prop => {
              this[config.aggregateKey][prop] = 0;
            });
          } else {
            Vue.set(this, config.aggregateKey, aggregateObj);
          }
        },

        /**
         * Update aggregate on add
         * @private
         */
        _fcmUpdateAggregateOnAdd(doc, config) {
          if (!config.aggregate || !doc) {
            return;
          }

          const aggregateObj = this[config.aggregateKey];
          if (!aggregateObj) {
            console.warn(`Aggregate object not found: ${config.aggregateKey}`);
            return;
          }

          config.aggregate.forEach(prop => {
            const value = getObjectValueByPath(doc, prop, 0);
            const numValue = typeof value === 'number' ? value : 0;
            aggregateObj[prop] = (aggregateObj[prop] || 0) + numValue;
          });
        },

        /**
         * Update aggregate on modify
         * @private
         */
        _fcmUpdateAggregateOnModify(newDoc, oldDoc, config) {
          if (!config.aggregate || !newDoc || !oldDoc) return;

          const aggregateObj = this[config.aggregateKey];
          if (!aggregateObj) {
            console.warn(`Aggregate object not found: ${config.aggregateKey}`);
            return;
          }

          config.aggregate.forEach(prop => {
            const oldValue = getObjectValueByPath(oldDoc, prop, 0);
            const newValue = getObjectValueByPath(newDoc, prop, 0);
            const diff = (typeof newValue === 'number' ? newValue : 0) - (typeof oldValue === 'number' ? oldValue : 0);
            aggregateObj[prop] = (aggregateObj[prop] || 0) + diff;
          });
        },

        /**
         * Update aggregate on remove
         * @private
         */
        _fcmUpdateAggregateOnRemove(doc, config) {
          if (!config.aggregate || !doc) return;

          const aggregateObj = this[config.aggregateKey];
          if (!aggregateObj) {
            console.warn(`Aggregate object not found: ${config.aggregateKey}`);
            return;
          }

          config.aggregate.forEach(prop => {
            const value = getObjectValueByPath(doc, prop, 0);
            const numValue = typeof value === 'number' ? value : 0;
            aggregateObj[prop] = (aggregateObj[prop] || 0) - numValue;
          });
        },

        /**
         * Handle collection snapshot
         * @private
         */
        _fcmHandleCollectionSnapshot(key, snapshot, config) {
          const items = this[key] || [];
          const itemsMap = new Map(items.map(item => [item.id, item]));

          snapshot.docChanges().forEach(change => {
            const docData = change.doc.data();
            const docId = change.doc.id;
            const newDoc = {id: docId, ...docData};

            // Apply transform if provided
            const transformedDoc = config.transform
              ? config.transform.call(this, newDoc)
              : newDoc;

            const context = {
              key,
              type: change.type,
              oldIndex: change.oldIndex,
              newIndex: change.newIndex
            };

            if (change.type === 'added') {
              // Update aggregate
              this._fcmUpdateAggregateOnAdd(transformedDoc, config);

              // Call onAdd hook with oldDoc (null for new items)
              if (config.onAdd) {
                config.onAdd.call(this, transformedDoc, null, context);
              }

              // Add to array at the correct index
              items.splice(change.newIndex, 0, transformedDoc);
              itemsMap.set(docId, transformedDoc);

            } else if (change.type === 'modified') {
              const oldDoc = itemsMap.get(docId);

              // Update aggregate
              this._fcmUpdateAggregateOnModify(transformedDoc, oldDoc, config);

              // Call onModify hook with oldDoc
              if (config.onModify) {
                config.onModify.call(this, transformedDoc, oldDoc, context);
              }

              // Remove from old position
              items.splice(change.oldIndex, 1);
              // Add to new position
              items.splice(change.newIndex, 0, transformedDoc);
              itemsMap.set(docId, transformedDoc);

            } else if (change.type === 'removed') {
              const oldDoc = itemsMap.get(docId);

              // Update aggregate
              this._fcmUpdateAggregateOnRemove(oldDoc, config);

              // Call onRemove hook with oldDoc
              if (config.onRemove) {
                config.onRemove.call(this, transformedDoc, oldDoc, context);
              }

              // Remove from array
              items.splice(change.oldIndex, 1);
              itemsMap.delete(docId);
            }
          });

          // Update reactive data
          Vue.set(this, key, items);
        },

        /**
         * Handle document snapshot
         * @private
         */
        _fcmHandleDocSnapshot(key, snapshot, config) {
          const oldDoc = this[key];

          if (snapshot.exists()) {
            const docData = snapshot.data();
            const newDoc = {id: snapshot.id, ...docData};

            // Apply transform if provided
            const transformedDoc = config.transform
              ? config.transform.call(this, newDoc)
              : newDoc;

            const context = {key, type: oldDoc ? 'modified' : 'added'};

            // Call appropriate hook
            if (!oldDoc && config.onAdd) {
              config.onAdd.call(this, transformedDoc, null, context);
            } else if (oldDoc && config.onModify) {
              config.onModify.call(this, transformedDoc, oldDoc, context);
            }

            Vue.set(this, key, transformedDoc);
          } else {
            // Document removed
            if (oldDoc && config.onRemove) {
              const context = {key, type: 'removed'};
              config.onRemove.call(this, null, oldDoc, context);
            }

            Vue.set(this, key, null);
          }
        }
      }
    });
  }
};

export default fcmReactiveBindPlugin;
